// THE FLOATING CORE's controller (#6779, prototype `:4202-4299`): whether it shows (checked
// every 120 ms against `pipShouldShow`, `fleet-live-pip.ts`), its size and collapsed state
// (a per-viewer preference), its resize by pointer and by key, and the pointer the eye
// follows. `FleetLivePip` renders it.
//
// ONE TIMER, ONE LISTENER: the 120 ms visibility check runs for the section's life, reading
// the Core panel's box and the page's own dialog state, never the DOM for a scrim. The
// document `pointermove` the eye follows is added when the floating Core shows and removed
// when it hides. While it shows it keeps the section's one frame loop awake (`keepAwake`).
//
// Under reduced motion show, hide and the collapse take effect at once; the floating Core's
// painter draws a still frame (`fleet-live-core-paint.ts`).

import { createSignal, onCleanup, onMount } from 'solid-js';
import type { LiveLoop } from './fleet-live-loop';
import {
  dragPipWidth,
  PIP_CHECK_MS,
  type PipSize,
  pipShouldShow,
  pipWidth,
  readPip,
  stepPipWidth,
  writePip,
} from './fleet-live-pip';
import type { FleetCue } from './fleet-live-sound';

const IN_EASE = 'cubic-bezier(0.16,1,0.3,1)';

/** An animation whose `finished` is never left to reject unheard when it is cancelled. */
function quiet(a: Animation): Animation {
  a.finished?.catch(() => {});
  return a;
}
const OUT_EASE = 'cubic-bezier(0.7,0,0.84,0)';
export const PIP_MS = { show: 560, hide: 380, morph: 420 } as const;

export type FloatingCoreDeps = {
  readonly loop: LiveLoop;
  /** The Core panel's wrap, when it is mounted. */
  readonly core: () => HTMLElement | undefined;
  readonly coreShown: () => boolean;
  readonly dialogOpen: () => boolean;
  readonly cue: (cue: FleetCue) => void;
};

export type FloatingCore = {
  /** Logically shown (it may still be animating out). */
  readonly shown: () => boolean;
  /** In the layout (`hidden` off). */
  readonly visible: () => boolean;
  readonly size: () => PipSize;
  readonly viewportW: () => number;
  readonly width: () => number;
  /** A pointer resize is running (the "W × H" tag shows). */
  readonly resizing: () => boolean;
  /** The pointer, in viewport px, and when it last moved (frame clock). */
  readonly pointer: () => { readonly x: number; readonly y: number; readonly at: number } | null;
  readonly attach: (el: HTMLElement) => void;
  readonly toggleMin: () => void;
  readonly dismiss: () => void;
  readonly resetWidth: () => void;
  readonly onResizeKey: (e: KeyboardEvent) => void;
  readonly onResizeDown: (e: PointerEvent) => void;
  readonly onResizeMove: (e: PointerEvent) => void;
  readonly onResizeUp: (e: PointerEvent) => void;
};

export function createFloatingCore(deps: FloatingCoreDeps): FloatingCore {
  const [shown, setShown] = createSignal(false);
  const [visible, setVisible] = createSignal(false);
  const [size, setSize] = createSignal<PipSize>(readPip());
  const [vw, setVw] = createSignal(globalThis.innerWidth ?? 1_440);
  let box: HTMLElement | undefined;
  let anim: Animation | undefined;
  let dismissed = false;
  let pointer: { x: number; y: number; at: number } | null = null;
  const moving = (): boolean => !deps.loop.reducedMotion() && typeof box?.animate === 'function';

  const onPointer = (e: PointerEvent): void => {
    pointer = { x: e.clientX, y: e.clientY, at: performance.now() };
  };

  const show = (): void => {
    if (shown()) return;
    setShown(true);
    setVisible(true);
    deps.loop.keepAwake(true);
    globalThis.document?.addEventListener('pointermove', onPointer, { passive: true });
    deps.cue('pipin');
    anim?.cancel();
    if (!moving() || box === undefined) return;
    anim = quiet(
      box.animate(
        [
          { opacity: 0, transform: 'translateY(28px) scale(0.82)' },
          { opacity: 1, transform: 'translateY(-4px) scale(1.02)', offset: 0.6 },
          { opacity: 1, transform: 'none' },
        ],
        { duration: PIP_MS.show, easing: IN_EASE },
      ),
    );
  };
  const hide = (): void => {
    if (!shown()) return;
    setShown(false);
    deps.loop.keepAwake(false);
    globalThis.document?.removeEventListener('pointermove', onPointer);
    pointer = null;
    deps.cue('pipout');
    anim?.cancel();
    if (!moving() || box === undefined) {
      setVisible(false);
      return;
    }
    anim = quiet(
      box.animate(
        [
          { opacity: 1, transform: 'none' },
          { opacity: 0.9, transform: 'scale(0.97)', offset: 0.45 },
          { opacity: 0, transform: 'translateY(20px) scale(0.85)' },
        ],
        { duration: PIP_MS.hide, easing: OUT_EASE },
      ),
    );
    anim.onfinish = () => {
      if (!shown()) setVisible(false);
    };
  };

  const check = (): void => {
    setVw(globalThis.innerWidth ?? vw());
    if (globalThis.document?.visibilityState === 'hidden') return;
    const core = deps.core();
    const box = core === undefined || core.hidden ? null : core.getBoundingClientRect();
    // A box with no height is not laid out (no layout engine, or not rendered yet).
    const rect = box === null || box.height === 0 ? null : box;
    const verdict = pipShouldShow({
      coreShown: deps.coreShown(),
      rect,
      dialogOpen: deps.dialogOpen(),
      dismissed,
    });
    dismissed = verdict.dismissed;
    if (verdict.show) show();
    else hide();
  };

  onMount(() => {
    const id = setInterval(check, PIP_CHECK_MS);
    onCleanup(() => {
      clearInterval(id);
      globalThis.document?.removeEventListener('pointermove', onPointer);
      deps.loop.keepAwake(false);
    });
  });

  /** Change the size, store it, and (when shown, motion on) morph from the old box. */
  const resize = (next: PipSize, morph: boolean): void => {
    const before = box?.getBoundingClientRect();
    setSize(next);
    writePip(next);
    deps.loop.invalidate();
    if (!morph || !moving() || !shown() || box === undefined || before === undefined) return;
    const after = box.getBoundingClientRect();
    quiet(
      box.animate(
        [
          { width: `${before.width}px`, height: `${before.height}px` },
          { width: `${after.width}px`, height: `${after.height}px` },
        ],
        { duration: PIP_MS.morph, easing: IN_EASE },
      ),
    );
  };

  const drag = createPipDrag({
    size,
    setSize,
    viewportW: vw,
    box: () => box,
    invalidate: () => deps.loop.invalidate(),
    cue: deps.cue,
  });

  return {
    shown,
    visible,
    size,
    viewportW: vw,
    width: () => pipWidth(size(), vw()),
    resizing: drag.resizing,
    pointer: () => pointer,
    attach: (el) => {
      box = el;
    },
    toggleMin: () => {
      const min = !size().min;
      resize({ ...size(), min }, true);
      deps.cue(min ? 'pipout' : 'pipin');
    },
    dismiss: () => {
      dismissed = true;
      hide();
    },
    resetWidth: () => resize({ ...size(), w: 0 }, true),
    onResizeKey: (e) => {
      const w = stepPipWidth(size(), e.key, e.shiftKey, vw());
      if (w === null) return;
      e.preventDefault();
      resize({ ...size(), w }, false);
    },
    onResizeDown: (e) => drag.down(e),
    onResizeMove: (e) => drag.move(e),
    onResizeUp: (e) => drag.up(e),
  };
}

/** The top-left handle's pointer resize: drag to size, store on release (prototype `:4237`). */
function createPipDrag(o: {
  readonly size: () => PipSize;
  readonly setSize: (s: PipSize) => void;
  readonly viewportW: () => number;
  readonly box: () => HTMLElement | undefined;
  readonly invalidate: () => void;
  readonly cue: (cue: FleetCue) => void;
}) {
  const [resizing, setResizing] = createSignal(false);
  let drag: { x: number; y: number; w: number; id: number } | null = null;
  return {
    resizing,
    down(e: PointerEvent): void {
      const box = o.box();
      if (o.size().min || box === undefined) return;
      e.preventDefault();
      (e.currentTarget as Element | null)?.setPointerCapture?.(e.pointerId);
      drag = { x: e.clientX, y: e.clientY, w: box.getBoundingClientRect().width, id: e.pointerId };
      setResizing(true);
      o.cue('lift');
    },
    move(e: PointerEvent): void {
      if (drag === null || e.pointerId !== drag.id) return;
      const w = dragPipWidth(drag.w, drag.x - e.clientX, drag.y - e.clientY, o.viewportW());
      o.setSize({ ...o.size(), w });
      o.invalidate();
    },
    up(e: PointerEvent): void {
      if (drag === null || e.pointerId !== drag.id) return;
      drag = null;
      setResizing(false);
      writePip(o.size());
      o.cue('dock');
    },
  };
}
